/** Shared helpers for Jira status transitions in list / detail pickers. */

export type JiraTransition = {
  id: string;
  name: string;
  to?: string;
};

export type StatusPickerItem = {
  status: string;
  transitionId: string | null;
  transitionName?: string;
  available: boolean;
  isCurrent: boolean;
};

/** Normalize for comparisons (accents, case, spacing). */
export function normalizeStatusName(status: string | null | undefined): string {
  return (status || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Code Review / In Code Review / CR family. */
export function isCodeReviewStatus(status: string | null | undefined): boolean {
  const n = normalizeStatusName(status);
  if (!n) return false;
  if (n === 'cr' || n === 'code review' || n === 'codereview') return true;
  return n.includes('code') && n.includes('review');
}

/**
 * Match filter status ↔ ticket status.
 * Exact (normalized) match, plus Code Review family aliases.
 */
export function statusesMatch(
  filterStatus: string | null | undefined,
  ticketStatus: string | null | undefined
): boolean {
  if (!filterStatus || !ticketStatus) return false;
  const a = normalizeStatusName(filterStatus);
  const b = normalizeStatusName(ticketStatus);
  if (a === b) return true;
  if (isCodeReviewStatus(a) && isCodeReviewStatus(b)) return true;
  return false;
}

export function isOpenStatus(status: string | null | undefined): boolean {
  const n = normalizeStatusName(status);
  return n === 'open' || n.includes('otevreno') || n === 'to do' || n === 'todo';
}

/** Workflow order used across Worklog / Tickets. Open always first. */
export function getStatusSortValue(status: string | null | undefined): number {
  const statusLower = normalizeStatusName(status);
  if (isOpenStatus(statusLower)) return 0;
  if (statusLower.includes('in progress') || statusLower.includes('rozpracovano')) return 2;
  if (
    statusLower.includes('ready for backend') ||
    statusLower.includes('ready for frontend')
  ) {
    return 3;
  }
  // Code Review before generic "review" / To Review
  if (isCodeReviewStatus(statusLower)) return 4;
  if (statusLower.includes('review') || statusLower.includes('revize')) return 5;
  if (statusLower.includes('test')) return 6;
  if (
    statusLower.includes('done') ||
    statusLower.includes('dokonceno') ||
    statusLower.includes('completed')
  ) {
    return 7;
  }
  if (statusLower.includes('cancel') || statusLower.includes('zrusen')) return 8;
  return 999;
}

/**
 * Put the transitions you typically use first:
 * next step in the workflow, then further ahead, then same/unknown, then backwards.
 */
export function sortTransitionsPreferred(
  transitions: JiraTransition[],
  currentStatus?: string | null
): JiraTransition[] {
  const current = getStatusSortValue(currentStatus);

  const score = (t: JiraTransition) => {
    const target = getStatusSortValue(t.to || t.name);
    if (target === 999) return 500;
    const dist = target - current;
    if (dist === 1) return 0; // immediate next status
    if (dist > 1) return dist; // further ahead
    if (dist === 0) return 40;
    return 100 - dist; // backwards last
  };

  return [...transitions].sort((a, b) => {
    const diff = score(a) - score(b);
    if (diff !== 0) return diff;
    return (a.name || '').localeCompare(b.name || '', 'cs');
  });
}

/**
 * Build a status picker from available Jira transitions only.
 * - Display name = target status (`to`), never the transition label / translation
 * - Unavailable statuses are omitted
 * - Open is sorted first
 * - Current status is included (marked) even without a self-transition
 */
export function buildStatusPickerItems(
  _allStatuses: string[],
  transitions: JiraTransition[],
  currentStatus?: string | null
): StatusPickerItem[] {
  const seen = new Set<string>();
  const items: StatusPickerItem[] = [];

  const pushUnique = (item: StatusPickerItem) => {
    const key = normalizeStatusName(item.status);
    if (!key || seen.has(key)) return;
    seen.add(key);
    items.push(item);
  };

  if (currentStatus?.trim()) {
    pushUnique({
      status: currentStatus.trim(),
      transitionId: null,
      available: false,
      isCurrent: true,
    });
  }

  for (const t of transitions) {
    // Prefer target status name — skip transition labels like "Start work"
    const statusName = (t.to || '').trim() || (t.name || '').trim();
    if (!statusName) continue;
    if (statusesMatch(statusName, currentStatus)) continue;

    pushUnique({
      status: statusName,
      transitionId: t.id,
      transitionName: t.name,
      available: true,
      isCurrent: false,
    });
  }

  return items.sort((a, b) => {
    // Open always first
    const aOpen = isOpenStatus(a.status);
    const bOpen = isOpenStatus(b.status);
    if (aOpen !== bOpen) return aOpen ? -1 : 1;

    const byWorkflow = getStatusSortValue(a.status) - getStatusSortValue(b.status);
    if (byWorkflow !== 0) return byWorkflow;
    return a.status.localeCompare(b.status, 'cs');
  });
}
